import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, computed, inject, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { ModalContentData } from '../modal-content';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../../notification/notification.service';
import { formatDateToYYYYMMDD } from '../../utils/formatting';
import { MatChipsModule } from '@angular/material/chips';
import { LocalStorageService } from '../../local-storage.service';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { Session } from '../../types/session.type';
import { SignalsStoreService } from '../../signals-store.service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { ResolutionService } from '../../resolution.service';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-pause-subscriptions',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatIconModule,
    ReactiveFormsModule,
    EmptyMessageComponent
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './pause-subscriptions.component.html',
  styleUrl: './pause-subscriptions.component.scss'
})
export class PauseSubscriptionsComponent implements OnInit {

  activeModal = inject(NgbActiveModal);
  notificarionService = inject(NotificationService);
  localStorageService = inject(LocalStorageService);
  signalsStoreService = inject(SignalsStoreService);
  #resolutionService = inject(ResolutionService);

  @ViewChild('picker') datePicker!: MatDateRangePicker<Date>;

  @Input()
  set data(data: ModalContentData | undefined) {
    if (data)
      this._data = data;
  }

  get data() {
    return this._data
  }

  private _data!: ModalContentData | undefined;

  minDate = signal(new Date());

  deliveryDay: string = '';

  daysSelected: any[] = [];

  selectedRanges: WritableSignal<{ start: any, end: any }[]> = signal([]);

  isMobile = computed(() => this.#resolutionService.isMobile());

  firstEnableDate: WritableSignal<Date | null> = signal(null);

  isEditMode = computed(() => {
    const pauseData = this.data?.pauseSubscription as any;
    return !!(pauseData?.existingRanges && pauseData.existingRanges.length > 0);
  });

  readonly range = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });

  nextDeliveryMessage = `
  <p class="m-0">
  <b>Important:</b>  Your next delivery after the pause will be scheduled by adding your subscription frequency (weekly, bi-weekly, monthly, etc.) to the end date of your pause.
  </p>
  `


  ngOnInit(): void {
    // Escuchar cambios en el rango de fechas para agregar automáticamente
    this.range.valueChanges.subscribe(value => {
      if (value.start && value.end) {
        this.addDateRange();
      }
    });

    // Load existing ranges if editing
    const pauseData = this.data?.pauseSubscription as any;
    if (pauseData?.existingRanges) {
      this.selectedRanges.set([...pauseData.existingRanges]);

      // Update daysSelected array with existing ranges
      pauseData.existingRanges.forEach((range: any) => {
        this.daysSelected.push(range.start, range.end);
      });
    }

    const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION) || null;
    if (session?.deliveryInfo) {
      this.deliveryDay = session.deliveryInfo.deliveryDay;
      let [year, month_str, day] = session.deliveryInfo.deliveryDate.split('-');
      if (this.data?.pauseSubscription?.minAvailableDate) {
        [year, month_str, day] = this.data?.pauseSubscription?.minAvailableDate.split('-');
      }
      const month = (+month_str) - 1;
      const aux_date = new Date(+year, month, +day);
      this.minDate.set(new Date(aux_date));
    }
  }

  isSelected = (event: any) => {
    if (!this.daysSelected) return;
    const date = `${("00" + (event.getMonth() + 1)).slice(-2)}/${("00" + event.getDate()).slice(-2)}/${event.getFullYear()}`;
    return this.daysSelected.find(x => x == date) ? "selected" : null as any;
  };

  dateFilter = (date: Date | null) => {
    if (!date) {
      return false;
    }

    if (!this.deliveryDay) return true;

    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    // const allowedDayIndex = daysOfWeek.indexOf(this.deliveryDay);


    let allowedDayIndex = -1;
    if (this.data?.pauseSubscription?.minAvailableDay)
      allowedDayIndex = daysOfWeek.indexOf(this.data?.pauseSubscription?.minAvailableDay);
    else
      allowedDayIndex = daysOfWeek.indexOf(this.deliveryDay);

    if (allowedDayIndex === -1)
      return true; // If the del

    // Filtrar por el día permitido
    const isCorrectDay = date.getDay() === allowedDayIndex;
    if (!isCorrectDay) return false;

    // Verificar que la fecha no esté dentro de ninguno de los rangos ya seleccionados
    const isInSelectedRange = this.selectedRanges()?.some(range => {
      const start = new Date(range.start);
      const end = new Date(range.end);

      // Normalizar tiempos a medianoche para evitar errores por horas
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      const current = new Date(date);
      current.setHours(0, 0, 0, 0);

      return current >= start && current <= end;
    });

    return !isInSelectedRange;
  }

  private setUpDateToYYYYMMDD(dateString: string): string {
    const arrayDate = dateString.split('/');
    return `${arrayDate[2]}-${arrayDate[0]}-${arrayDate[1]}T00:00:00`;
  }

  validatePauseDate() {
    if (!(this.selectedRanges().length)) {
      if (this.isEditMode())
        return this.closeModalAndSendData([]);
      return this.notificarionService.show({ text: 'You must select at least a date range to pause your subscription.', type: 'error' })
    }

    const dates = this.selectedRanges().map(e => {
      return {
        start: new Date(this.setUpDateToYYYYMMDD(e.start)).toISOString().split('T')[0],
        end: new Date(this.setUpDateToYYYYMMDD(e.end)).toISOString().split('T')[0],
      }
    });

    this.closeModalAndSendData(dates);
  }

  private closeModalAndSendData(dates: { start: any, end: any }[]) {
    this.activeModal.close({ dates, delete: !dates.length && this.isEditMode() });
  }

  addDateRange() {
    const { start: startDate, end: endDate } = this.range.value;

    // Verificar que ambas fechas estén seleccionadas
    if (!startDate || !endDate) {
      return;
    }

    const start = `${("00" + (startDate!.getMonth() + 1)).slice(-2)}/${("00" + startDate!.getDate()).slice(-2)}/${startDate!.getFullYear()}`;
    const end = `${("00" + (endDate!.getMonth() + 1)).slice(-2)}/${("00" + endDate!.getDate()).slice(-2)}/${endDate!.getFullYear()}`;
    this.selectedRanges.update((current) => {
      current.push({
        start,
        end
      });

      return current
    });

    // Cerrar el datepicker si está abierto
    if (this.datePicker?.opened) {
      this.datePicker.close();
    }

    // Usar setTimeout para asegurar que el datepicker se cierre completamente antes de limpiar
    setTimeout(() => {
      this.range.patchValue({
        start: null,
        end: null
      });
      this.range.markAsUntouched();
      this.range.markAsPristine();
    }, 0);

    this.getFirstEnabledDate(this.minDate())
    this.daysSelected.push(start, end);
  }

  removeDateRange(index: number) {
    const { start, end } = this.selectedRanges()[index];

    const startIndex = this.daysSelected.findIndex(d => d === start);
    if (startIndex > -1) this.daysSelected.splice(startIndex, 1);

    const endIndex = this.daysSelected.findIndex(d => d === end);
    if (endIndex > -1) this.daysSelected.splice(endIndex, 1);

    this.selectedRanges.update((current) => {
      current.splice(index, 1);
      return current;
    })

    this.getFirstEnabledDate(this.minDate());
  }

  getFirstEnabledDate(fromDate: Date = new Date()) {
    const maxLookAheadDays = 365; // avoid infinite loops
    let date = new Date(fromDate);

    for (let i = 0; i < maxLookAheadDays; i++) {
      if (this.dateFilter(date)) {
        this.firstEnableDate.set(date);
        return;
      }
      date.setDate(date.getDate() + 1);
    }

    // fallback: today
    this.firstEnableDate.set(fromDate);
  }

}
